/**
 * 外部の監視・計測サービスへ送る URL から落とすクエリキー。
 *
 * いずれも「持っているだけで他人のリソースを操作できる」か「秘匿前提の鍵」であり、
 * 第三者かつ長期保持のサービスに残してはならない。
 *
 * - session: 3DS の予約確定に使うトークン
 * - draftKey: microCMS のプレビュー鍵
 * - reservationId: 推測困難さだけでキャンセルページを保護している識別子
 * - unsubscribeToken: 所持しているだけでウェイトリストの購読を停止できるトークン
 *
 * 総称的なキー（id 等）を足さないこと。除去はサイト全体に効くため、
 * 将来そのキーを使う任意のページで URL が無言で欠ける。
 */
export const SENSITIVE_QUERY_KEYS = ['session', 'draftKey', 'reservationId', 'unsubscribeToken'];

/**
 * 相対パスを URL として解釈するためのダミー origin。
 *
 * Sentry の navigation breadcrumb は同一オリジンだと相対パス（`/path?query`）で渡ってくる。
 * 絶対 URL しか解釈できないと、除去がもっとも要る自サイト内の遷移で素通しになる。
 * `.invalid` は実在しない TLD なので、実 URL の origin と衝突しない。
 */
const RELATIVE_BASE = 'http://relative.invalid';

/**
 * URL から秘匿すべきクエリを取り除く。
 *
 * GA4 の page_location と Sentry のイベント URL・breadcrumb・Replay で共有する。
 * ロジックが分かれると、キーを足したときに片方の更新が漏れる。
 *
 * 相対パスで渡されたものは相対のまま返す。絶対 URL 化して返すとダミー origin が混ざる。
 */
export const sanitizeSensitiveQuery = (href: string): string => {
  try {
    const url = new URL(href, RELATIVE_BASE);
    const hits = SENSITIVE_QUERY_KEYS.filter((key) => url.searchParams.has(key));
    // 該当が無ければ href をそのまま返す。URL を組み直すと無関係なページで表記が揺れる
    if (hits.length === 0) return href;
    hits.forEach((key) => url.searchParams.delete(key));
    const isRelative = url.origin === RELATIVE_BASE && !href.startsWith(RELATIVE_BASE);
    return isRelative ? `${url.pathname}${url.search}${url.hash}` : url.toString();
  } catch {
    // URL として解釈できなければ判定できないため素通しする
    return href;
  }
};

/** 丸括弧を境界に含めるのは、スタックトレースが `at fn (https://...:1:2)` の形で URL を括るため */
const URL_IN_TEXT = /https?:\/\/[^\s"'<>()]+/g;

/** スタックトレースのように、1 本の URL に収まらない文字列から秘匿クエリを取り除く */
export const sanitizeSensitiveQueryInText = (text: string): string => text.replace(URL_IN_TEXT, (url) => sanitizeSensitiveQuery(url));
